<div class="dl-page">
  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Usage</h1>
    </div>
  </div>

  <mat-tab-group class="usage-tabs" animationDuration="0ms">
    <mat-tab label="Users">
      <div class="tab-body">
        <div class="tab-header">
          <p class="dl-page__subtitle" *ngIf="generatedAt">
            Data as of {{ generatedAt | date:'short' }} — cached for up to 10 minutes.
          </p>
          <button type="button" class="dl-btn dl-btn--icon" (click)="refresh()" [disabled]="refreshing"
                  matTooltip="Rebuild the snapshot" aria-label="Rebuild the snapshot">
            <mat-icon *ngIf="!refreshing">refresh</mat-icon>
            <mat-icon *ngIf="refreshing" class="usage-spin">hourglass_empty</mat-icon>
          </button>
        </div>

        <div *ngIf="loading" class="dl-loading">
          <div class="dl-spinner"></div>
          <p>Loading usage data…</p>
        </div>

        <div *ngIf="error" class="dl-banner dl-banner--error">
          <mat-icon>error</mat-icon>
          <div class="dl-banner__body">{{ error }}</div>
          <div class="dl-banner__actions">
            <button type="button" class="dl-btn dl-btn--sm" (click)="load()">
              <mat-icon>refresh</mat-icon>
              Try again
            </button>
          </div>
        </div>

        <div *ngIf="!loading && !error" [class.dl-is-refreshing]="refreshing">

          <!-- Everything below the banner depends on activity data. When Keycloak cannot supply it,
               showing the figures anyway would state "nobody has ever used the system" as fact. -->
          <div *ngIf="!lastActiveAvailable" class="dl-banner dl-banner--warning">
            <mat-icon>warning</mat-icon>
            <div>
              <strong>Activity data is unavailable.</strong>
              Keycloak did not return last-active information, so activity figures are hidden rather
              than shown as zero. The user list below is still accurate.
            </div>
          </div>

          <ng-container *ngIf="lastActiveAvailable">
            <div class="dl-stat-grid">
              <div class="dl-stat">
                <div class="dl-stat__value">{{ totals.users }}</div>
                <div class="dl-stat__label">Total users</div>
                <div class="dl-stat__sub">across {{ rollups.length }} tenants</div>
              </div>
              <div class="dl-stat">
                <div class="dl-stat__value">{{ totals.activeToday }}</div>
                <div class="dl-stat__label" matTooltip="Since midnight in your local timezone">Active today</div>
                <div class="dl-stat__sub">{{ percentOf(totals.activeToday) }}</div>
              </div>
              <div class="dl-stat">
                <div class="dl-stat__value">{{ totals.activeWeek }}</div>
                <div class="dl-stat__label">Active in last 7 days</div>
                <div class="dl-stat__sub">{{ percentOf(totals.activeWeek) }}</div>
              </div>
              <div class="dl-stat">
                <div class="dl-stat__value">{{ totals.activeMonth }}</div>
                <div class="dl-stat__label">Active in last 30 days</div>
                <div class="dl-stat__sub">{{ percentOf(totals.activeMonth) }}</div>
              </div>
              <div class="dl-stat">
                <div class="dl-stat__value">{{ totals.never }}</div>
                <div class="dl-stat__label">Never active</div>
                <div class="dl-stat__sub">{{ percentOf(totals.never) }}</div>
              </div>
            </div>
            <p class="usage-note">
              These four activity figures are cumulative — someone active today is also counted in the
              7- and 30-day windows. “Today” means since your local midnight; the 7- and 30-day windows
              roll back from now.
            </p>

            <div class="charts">
              <div class="dl-card usage-chart-card">
                <h3>Users per tenant</h3>
                <p class="usage-chart-sub">Bar length is the tenant’s total users, split by whether they have been active in the last 30 days.</p>
                <div class="chart-host">
                  <ngx-charts-bar-horizontal-stacked
                    [results]="tenantChartData"
                    [scheme]="tenantScheme"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="true"
                    legendTitle=""
                    [showDataLabel]="true"
                    [barPadding]="6">
                  </ngx-charts-bar-horizontal-stacked>
                </div>
                <p class="usage-chart-note" *ngIf="chartTailNote">{{ chartTailNote }}</p>
              </div>

              <div class="dl-card usage-chart-card">
                <h3>When users were last active</h3>
                <p class="usage-chart-sub">One bar per group. Unlike the tiles above these groups are exclusive, so they add up to {{ totals.users }}.</p>
                <div class="chart-host">
                  <ngx-charts-bar-horizontal
                    [results]="bucketChartData"
                    [scheme]="bucketScheme"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="false"
                    [roundEdges]="false"
                    [showDataLabel]="true"
                    [barPadding]="6">
                  </ngx-charts-bar-horizontal>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- Per-tenant rollup -->
          <div class="dl-card">
            <div class="dl-card__header">
              <h3>Usage by tenant</h3>
            </div>
            <div class="dl-table-scroll">
              <table class="dl-table dl-table--cards" dlCardTable>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Users</th>
                    <th *ngIf="lastActiveAvailable">Active last 7 days</th>
                    <th *ngIf="lastActiveAvailable">Active last 30 days</th>
                    <th *ngIf="lastActiveAvailable">Most recent activity</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let rollup of rollups; trackBy: trackByTenantId">
                    <td class="dl-cell--title">
                      <a [routerLink]="['/partners', rollup.id]" class="tenant-link">{{ rollup.name }}</a>
                      <span class="no-group" *ngIf="!rollup.groupExists"
                            matTooltip="This tenant has no Keycloak group yet, so no users have been provisioned.">
                        no accounts yet
                      </span>
                    </td>
                    <td>{{ rollup.users }}</td>
                    <td *ngIf="lastActiveAvailable">{{ rollup.activeWeek }}</td>
                    <td *ngIf="lastActiveAvailable">{{ rollup.activeMonth }}</td>
                    <td *ngIf="lastActiveAvailable" [class.muted]="rollup.mostRecentMs === null">
                      {{ rollup.mostRecentText }}
                    </td>
                  </tr>
                  <tr *ngIf="rollups.length === 0">
                    <td colspan="5" class="no-data">No tenants found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Flat user list -->
          <div class="dl-card">
            <div class="dl-card__header">
              <h3>All users</h3>
              <span class="dl-card__subtitle">{{ filteredUsers.length }} of {{ totals.users }}</span>
            </div>

            <!-- Filtering here is pure array work against data already in memory: no request, so no
                 debounce and nothing to disable while it runs. -->
            <div class="dl-filters usage-filters">
              <div class="dl-field">
                <label class="dl-field__label" for="usageSearch">Search</label>
                <input id="usageSearch" type="text" [(ngModel)]="searchTerm" (input)="applyFilters()"
                       placeholder="Name, email or tenant" class="dl-input" />
              </div>
              <div class="dl-field">
                <label class="dl-field__label" for="usageTenant">Tenant</label>
                <select id="usageTenant" class="dl-select" [(ngModel)]="tenantFilter" (change)="applyFilters()">
                  <option value="">All tenants</option>
                  <option *ngFor="let tenant of tenantOptions" [value]="tenant.id">{{ tenant.name }}</option>
                </select>
              </div>
              <div class="dl-field">
                <label class="dl-field__label" for="usageRole">Role</label>
                <select id="usageRole" class="dl-select" [(ngModel)]="roleFilter" (change)="applyFilters()">
                  <option value="">All roles</option>
                  <option *ngFor="let role of roleOptions" [value]="role">{{ roleLabelFor(role) }}</option>
                </select>
              </div>
              <div class="dl-field">
                <label class="dl-field__label" for="usageStatus">Status</label>
                <select id="usageStatus" class="dl-select" [(ngModel)]="statusFilter" (change)="applyFilters()">
                  <option value="">All statuses</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div class="dl-field" *ngIf="lastActiveAvailable">
                <label class="dl-field__label" for="usageBucket">Last active</label>
                <select id="usageBucket" class="dl-select" [(ngModel)]="bucketFilter" (change)="applyFilters()">
                  <option value="">Any time</option>
                  <option *ngFor="let option of bucketOptions" [value]="option[0]">{{ option[1] }}</option>
                </select>
              </div>
              <div class="dl-field usage-filters__action">
                <button type="button" class="dl-btn dl-btn--ghost" (click)="clearFilters()">
                  <mat-icon>clear</mat-icon>
                  Clear
                </button>
              </div>
            </div>

            <div class="dl-table-scroll">
              <table class="dl-table dl-table--cards" dlCardTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Tenant</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th *ngIf="lastActiveAvailable">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let user of filteredUsers; trackBy: trackByUserId">
                    <td class="dl-cell--title user-name">{{ user.name || '—' }}</td>
                    <td class="user-email">
                      <a *ngIf="user.email" [href]="'mailto:' + user.email">{{ user.email }}</a>
                      <span *ngIf="!user.email" class="muted">—</span>
                    </td>
                    <td>
                      <a [routerLink]="['/partners', user.tenantId]" class="tenant-link" *ngIf="user.tenantId != null">
                        {{ user.tenantLabel }}
                      </a>
                      <span *ngIf="user.tenantId == null" class="muted">{{ user.tenantLabel }}</span>
                    </td>
                    <td><span class="user-role" [ngClass]="user.roleClass">{{ user.roleLabel }}</span></td>
                    <td><span class="user-status" [ngClass]="user.statusClass">{{ user.statusLabel }}</span></td>
                    <td *ngIf="lastActiveAvailable" [class.muted]="user.lastActiveMs === null" [matTooltip]="user.lastActiveTitle">
                      {{ user.lastActiveText }}
                    </td>
                  </tr>
                  <tr *ngIf="filteredUsers.length === 0">
                    <td colspan="6" class="no-data">No users match the current filters.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Feature usage">
      <div class="tab-body">
        <app-feature-usage></app-feature-usage>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
